/**
 * R0.4: Change password page. Shown when mustChangePassword is true (first login with temp password).
 * Uses react-hook-form; submits to POST /api/v1/auth/change-password.
 */
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { authApi } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const PASSWORD_RULES = 'Minimo 10 caratteri, almeno 3 tra: maiuscole, minuscole, numeri, simboli (!@#$%^&* ecc.).';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { refreshOnce } = useAuth();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: FormValues) => {
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      await refreshOnce();
      showMessage('Password aggiornata. Puoi procedere.', 'success');
      navigate('/app/dashboard', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Errore durante il cambio password.';
      showMessage(msg, 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 440, mx: 'auto', py: 3, px: 2 }}>
      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ mb: 0 }}>
            Cambio password obbligatorio al primo accesso.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            {PASSWORD_RULES}
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Password attuale"
                type="password"
                autoComplete="current-password"
                {...register('currentPassword', { required: 'Obbligatorio' })}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
                size="small"
                fullWidth
              />
              <TextField
                label="Nuova password"
                type="password"
                autoComplete="new-password"
                {...register('newPassword', { required: 'Obbligatorio' })}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                size="small"
                fullWidth
              />
              <TextField
                label="Conferma nuova password"
                type="password"
                autoComplete="new-password"
                {...register('confirmNewPassword', {
                  required: 'Obbligatorio',
                  validate: (v) => v === newPassword || 'Le password non coincidono',
                })}
                error={!!errors.confirmNewPassword}
                helperText={errors.confirmNewPassword?.message}
                size="small"
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvataggio…' : 'Cambia password'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
